import { useEffect, useState } from 'react';
import { DataCertification } from 'types/data-certification';
import { GetDataCertification } from 'api/data-certifications-endpoint';
import DataCertificationButton from './DataCertificationButton';
import { canViewCertificationsForAnyDistrictAndSchool } from '../../utilities/AuthService';

type DataCertificationInfoProps = {
  districtId: string;
  schoolId: string;
  needReload: boolean;
  setNeedReload: (value: boolean) => void;
};

export default function DataCertificationInfo(props: DataCertificationInfoProps): JSX.Element {
  const [dataCertification, setDataCertification] = useState<DataCertification>();
  const [loading, setLoading] = useState(true);
  const [failedToGetCertificationStatus, setFailedToGetCertificationStatus] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const currentUserCanViewCertificationsForAnyDistrictAndSchool = canViewCertificationsForAnyDistrictAndSchool();

  useEffect(() => {
    async function fetchDataCertificationForCurrentUser() {
      if (currentUserCanViewCertificationsForAnyDistrictAndSchool && !props.districtId && !props.schoolId) {
        return;
      }
      props.setNeedReload(false);
      setLoading(true);
      const currentDistrictId = props.districtId;
      const currentSchoolId = props.schoolId;
      let queryParams = '';
      if (currentDistrictId != '') {
        queryParams += `districtId=${currentDistrictId}&`;
      }
      if (currentSchoolId != '') {
        queryParams += `schoolId=${currentSchoolId}`;
      }
      try {
        const result = await GetDataCertification(queryParams);
        if (result.success) {
          setDataCertification(result.data);
          setErrorMessage('');
          setFailedToGetCertificationStatus(false);
        } else {
          if (result.message === 'Data has never been certified.') {
            setDataCertification(undefined);
            setErrorMessage('');
            setFailedToGetCertificationStatus(false);
          } else {
            setFailedToGetCertificationStatus(true);
            setErrorMessage(result.message || 'Failed to load certification status');
          }
        }
      } catch (error) {
        setErrorMessage('Failed to load certification status');
        setFailedToGetCertificationStatus(true);
      } finally {
        setLoading(false);
      }
    }

    fetchDataCertificationForCurrentUser();
  }, [props.needReload, props.districtId, props.schoolId]);

  function onCertificationError(errorFromCertificationCall: string) {
    setErrorMessage(errorFromCertificationCall);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {loading && (
        <div
          style={{
            margin: 'auto 0px auto auto',
            fontSize: '.8rem',
            color: '#555',
          }}
        >
          Loading Data Certification Status...
        </div>
      )}

      {errorMessage && (
        <div
          style={{
            margin: 'auto 0px auto auto',
            fontSize: '.8rem',
            color: '#a44',
          }}
        >
          {errorMessage}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          <span style={{ margin: 'auto 0 2px 0' }}>This data is</span>
          {dataCertification && dataCertification.IsCertified ? (
            <div>
              <span
                style={{
                  margin: '0px 8px 0px',
                  color: '#185bc9',
                  fontWeight: 'bold',
                  fontSize: '18px',
                }}
              >
                Certified
              </span>
              <span
                style={{
                  color: '#777',
                  fontSize: '.8rem',
                  margin: 'auto 0 2px auto',
                }}
              >
                ({dataCertification.LastModifiedDateTime})
              </span>
            </div>
          ) : (
            <span style={{ margin: 'auto 8px 2px 6px', fontWeight: 'bold' }}>not certified</span>
          )}
        </div>
        {!failedToGetCertificationStatus && !loading && (
          <DataCertificationButton
            onError={onCertificationError}
            onSuccess={() => {
              props.setNeedReload(true);
            }}
            setLoading={setLoading}
            dataCertification={dataCertification}
            districtId={props.districtId}
            schoolId={props.schoolId}
          />
        )}
      </div>
    </div>
  );
}
